import { useState } from "react";
import { BellIcon, CheckIcon } from "@phosphor-icons/react";
import { Button, ButtonLink } from "../components/Button";
import type { AlertEvent } from "../api/types";
import { errorMessage } from "../api/client";
import { useToast } from "../components/Toast";
import { displayTitle } from "../lib/alertTitle";
import { parseTs } from "../lib/time";
import { isPanic, useRealtime } from "../realtime/RealtimeProvider";

// The latest alerts, floating right of the hologram (mockup #alertPanel).

const clockFmt = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
const dayFmt = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });

/** "9:15" today (no AM/PM, like the mockup), "sep 21" before today. */
export function shortTime(iso: string): string {
  const t = parseTs(iso);
  if (t === null) return "--";
  const d = new Date(t);
  const now = new Date();
  if (d.toDateString() !== now.toDateString()) return dayFmt.format(d).toLowerCase();
  return clockFmt
    .formatToParts(d)
    .filter((p) => p.type !== "dayPeriod")
    .map((p) => p.value)
    .join("")
    .trim();
}

export function AlertsPanel({ events, error, loading, max = 4 }: { events: AlertEvent[]; error: string | null; loading: boolean; max?: number }) {
  const { ack } = useRealtime();
  const toast = useToast();
  const [acking, setAcking] = useState<Record<string, boolean>>({});
  const open = events.filter((e) => !e.acked_by).length;
  // Unacknowledged first (newest first), then the rest: what needs review is always visible.
  const shown = [...events.filter((e) => !e.acked_by), ...events.filter((e) => e.acked_by)].slice(0, max);

  const onAck = async (e: AlertEvent) => {
    setAcking((a) => ({ ...a, [e.id]: true }));
    try {
      await ack(e.id);
    } catch (err) {
      toast.show(`Ack failed. ${errorMessage(err)}`, "error");
    } finally {
      setAcking((a) => ({ ...a, [e.id]: false }));
    }
  };

  return (
    <section className="float right alertpanel" aria-labelledby="alerts-title">
      <div className="ftitle">
        <b id="alerts-title">alerts</b>
        <span>{open ? `${open} to review` : events.length ? "all reviewed" : ""}</span>
      </div>
      {error && events.length === 0 ? (
        <p className="alerts__empty red-text">{error}</p>
      ) : loading && events.length === 0 ? (
        <p className="alerts__empty muted">loading…</p>
      ) : shown.length === 0 ? (
        <p className="alerts__empty muted">No alerts yet. Button presses, panic alerts and motion show up here.</p>
      ) : (
        <ul className="alerts">
          {shown.map((e) => {
            const hot = !e.acked_by && isPanic(e);
            return (
              <li key={e.id} className={hot ? "hot" : undefined}>
                <time dateTime={e.created_at}>{shortTime(e.created_at)}</time>
                <span className="t" title={displayTitle(e.title)}>
                  {displayTitle(e.title)}
                </span>
                {e.acked_by ? (
                  <span className="s">acked · {e.acked_by.name.toLowerCase()}</span>
                ) : (
                  <Button
                    size="sm"
                    variant="primary"
                    icon={CheckIcon}
                    className="ack"
                    onClick={() => void onAck(e)}
                    disabled={acking[e.id]}
                    aria-label={`Acknowledge ${displayTitle(e.title)}`}
                  >
                    {acking[e.id] ? "…" : "ack"}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <ButtonLink to="/alerts" state={{ fromScene: true }} size="sm" icon={BellIcon} className="more">
        all alerts
      </ButtonLink>
    </section>
  );
}
